import { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet, useNavigate } from "react-router";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { getBackgroundJobKindList } from "@/api/background-job/getBackgroundJobKindList";
import { getBackgroundJobPage } from "@/api/background-job/getBackgroundJobPage";
import { isBackgroundJobActive } from "@/api/background-job/_models/BackgroundJob";
import { ListGrid } from "@/components/list-grid/ListGrid";
import { Page } from "@/components/Page";
import { PageHeader } from "@/components/PageHeader";
import { ListGridFooter } from "@/components/list-grid/ListGridFooter";
import { toErrorText } from "@/components/snapshot/snapshot.utils";
import { DETAIL_KIND, toDetailLink } from "@/features/detail/detailKind";
import { JOB_LIST_PATH } from "@/features/job/job.paths";
import { toJobKindLabel } from "@/features/job/job.utils";
import { isJobListFilterActive, JOB_LIST_EMPTY_FILTER, JobListFilterPanel } from "./job-list-filter/JobListFilterPanel";
import type { JobListFilterValues } from "./job-list-filter/JobListFilterPanel";
import { JobListHeader } from "./JobListHeader";
import { JobListRow } from "./JobListRow";
import { JobListSkeletonRow } from "./JobListSkeletonRow";

const PAGE_SIZES = [50, 100, 200];
const DEFAULT_PAGE_SIZE = 50;
const ACTIVE_POLL_MS = 3000;

/**
 * The Jobs page: background jobs that have run or are running, newest first, paged and filterable.
 * Rows open to the job's input and its result or error; polls while a job on the page is still going.
 */
export const JobList = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<JobListFilterValues>(JOB_LIST_EMPTY_FILTER);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [openIds, setOpenIds] = useState<string[]>([]);
  const [now, setNow] = useState(() => Date.now());
  const pageArgs = useMemo(() => {
    return {
      kind: filter.kind || undefined,
      statuses: filter.statuses,
      trigger: filter.trigger || undefined,
      offset: page * pageSize,
      limit: pageSize,
    };
  }, [filter, page, pageSize]);
  const query = getBackgroundJobPage.useQuery(pageArgs);
  const jobs = query.data?.backgroundJobs ?? [];
  const total = query.data?.total ?? 0;
  const isAnyActive = jobs.some((job) => isBackgroundJobActive(job));
  getBackgroundJobPage.useQuerySubscription(pageArgs, { pollingInterval: isAnyActive ? ACTIVE_POLL_MS : 0 });
  const kindsQuery = getBackgroundJobKindList.useQuery();
  const kinds = kindsQuery.data ?? [];
  const isFiltered = isJobListFilterActive(filter);
  let footerSummary = `${total.toLocaleString()} jobs`;
  if (isFiltered) footerSummary = `${total.toLocaleString()} jobs match the filters`;
  let emptyText: string | null = null;
  if (jobs.length === 0 && !query.isFetching) {
    emptyText = "No jobs yet — run one (▶ at the end of the headings), or a build, import or schedule will add them here.";
    if (isFiltered) emptyText = "No jobs match these filters.";
  }

  const handleRunOpen = () => {
    navigate(toDetailLink(JOB_LIST_PATH, DETAIL_KIND.run));
  };

  const handleFilterToggle = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  const handleFilterClose = () => {
    setIsFilterOpen(false);
  };

  const handleFilterChange = (nextFilter: JobListFilterValues) => {
    setFilter(nextFilter);
    setPage(0);
  };

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
  };

  const handlePageSizeChange = (nextPageSize: number) => {
    setPageSize(nextPageSize);
    setPage(0);
  };

  const handleSelect = useCallback((jobId: string) => {
    navigate(toDetailLink(JOB_LIST_PATH, DETAIL_KIND.jobs, jobId));
  }, [navigate]);

  const handleToggle = useCallback((jobId: string) => {
    setOpenIds((previous) => {
      if (previous.includes(jobId)) return previous.filter((id) => id !== jobId);
      return [...previous, jobId];
    });
  }, []);

  useEffect(() => {
    if (!isAnyActive) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [isAnyActive]);

  if (query.isError) {
    return (
      <Alert severity="error">
        Failed to load jobs: {toErrorText(query.error)}
      </Alert>
    );
  }

  return (
    <>
      <JobListFilterPanel
        filter={filter}
        isOpen={isFilterOpen}
        kinds={kinds}
        onChange={handleFilterChange}
        onClose={handleFilterClose}
      />
      <Page isLocked>
        <PageHeader title="Jobs">
          <Button
            disableElevation
            onClick={handleRunOpen}
            size="small"
            startIcon={<PlayArrowIcon />}
            variant="contained"
          >
            Run a job
          </Button>
        </PageHeader>
        <ListGrid
          emptyText={emptyText}
          footer={(
            <ListGridFooter
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              page={page}
              pageSize={pageSize}
              pageSizes={PAGE_SIZES}
              summary={footerSummary}
              total={total}
            />
          )}
          header={(
            <JobListHeader
              isFilterActive={isFiltered}
              onFilterToggle={handleFilterToggle}
            />
          )}
          isFetching={query.isFetching}
          isLoading={query.isLoading}
          skeletonRow={<JobListSkeletonRow />}
        >
          {jobs.map((job) => (
            <JobListRow
              isOpen={openIds.includes(job.id)}
              job={job}
              jobLabel={toJobKindLabel(kinds, job.kind)}
              kind={kinds.find((entry) => entry.kind === job.kind)}
              key={job.id}
              now={now}
              onSelect={handleSelect}
              onToggle={handleToggle}
            />
          ))}
        </ListGrid>
      </Page>
      <Outlet />
    </>
  );
};
